import React, { useEffect, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import { reassigneParticipant } from "../../../../services/participantServices/ParticipantService";
import { getTaskLKValues } from "../../../../services/taskServices/TaskLKService";
import TaskCreationLKValues from "../../../../interfaces/lkValues/TaskCreationLKValues";
import { ActiveParticipantDTO } from "../../../../interfaces/Participant";
import "./AddParticipantModal.css";

interface OptionType {
  value: string;
  label: string;
}

interface TransferParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  participant: ActiveParticipantDTO | null;
  participants?: ActiveParticipantDTO[]; // Optional list of participants for selection
}

const TransferParticipantModal: React.FC<TransferParticipantModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  participant,
  participants = [],
}) => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [assigneeOptions, setAssigneeOptions] = useState<OptionType[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<string>("");
  const [selectedParticipant, setSelectedParticipant] = useState<ActiveParticipantDTO | null>(participant);
  const [participantOptions, setParticipantOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    const fetchLkValues = async () => {
      if (currentUser && isOpen) {
        const authToken: string = await currentUser.getIdToken();
        const fetchedValues: TaskCreationLKValues | null =
          await getTaskLKValues(authToken);
        if (fetchedValues) {
          setAssigneeOptions(
            fetchedValues.users.map((user) => ({
              value: user.id.toString(),
              label: `${user.firstName} ${user.lastName}`,
            }))
          );
        }
      }
    };
    fetchLkValues();
  }, [currentUser, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedAssignee("");
      setSelectedParticipant(participant);
      // Build participant options if participants list is provided
      if (participants && participants.length > 0) {
        setParticipantOptions(
          participants.map((p) => ({
            value: p.id.toString(),
            label: `${p.firstName} ${p.lastName}`,
          }))
        );
      }
    }
  }, [isOpen, participant, participants]);

  const handleAssigneeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAssignee(e.target.value);
  };

  const handleParticipantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const participantId = e.target.value;
    if (participantId && participants) {
      const found = participants.find((p) => p.id.toString() === participantId);
      setSelectedParticipant(found || null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const participantToTransfer = selectedParticipant || participant;
    
    if (!participantToTransfer || !selectedAssignee) {
      if (!participantToTransfer) {
        toast.error("Please select a participant to transfer");
      } else {
        toast.error("Please select a support coordinator to transfer to");
      }
      return;
    }

    setIsLoading(true);
    try {
      const authToken = await currentUser?.getIdToken();
      if (!authToken) {
        toast.error("Authentication error");
        return;
      }

      const response = await reassigneParticipant(
        participantToTransfer.id.toString(),
        selectedAssignee,
        authToken
      );

      if (response === true) {
        toast.success(`Successfully transferred ${participantToTransfer.firstName} ${participantToTransfer.lastName}`);
        handleCancel();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error("Failed to transfer participant");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to transfer participant");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedAssignee("");
    setSelectedParticipant(participant);
    onClose();
  };

  if (!isOpen) return null;

  const participantToTransfer = selectedParticipant || participant;
  if (!participantToTransfer && (!participants || participants.length === 0)) return null;

  const participantName = participantToTransfer ? `${participantToTransfer.firstName} ${participantToTransfer.lastName}` : "";
  const currentCoordinator = participantToTransfer?.caseworkers || "N/A";

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        onClick={handleCancel}
      >
        <div
          className="relative w-full max-w-[30rem] rounded-2xl bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#E5E7EB] py-4 px-6">
            <h2 className="" style={{
              color: "#0A0A0A",
              fontFamily: "Poppins",
              fontSize: "1.25rem",
              fontWeight: 700,
              lineHeight: "1.75rem"
            }}>
              Transfer Participant
            </h2>
            <button
              onClick={handleCancel}
              className="flex items-center text-[#0A0A0A] hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Participant Selection (only show if no participant is pre-selected) */}
                {!participant && participants && participants.length > 0 && (
                  <div>
                    <label className="block text-base font-semibold text-[#364153] mb-2">
                      Select Participant
                    </label>
                    <div className="relative">
                      <select
                        value={selectedParticipant?.id.toString() || ""}
                        onChange={handleParticipantChange}
                        className="w-full px-3 py-2 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500 appearance-none bg-white"
                        required
                      >
                        <option value="">Select a participant</option>
                        {participantOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>
                )}

                {/* Participant Name */}
                {participantToTransfer && (
                  <>
                    <div>
                      <label className="block text-base font-semibold text-[#364153] mb-2">
                        Participant Name
                      </label>
                      <input
                        type="text"
                        value={participantName}
                        readOnly
                        className="w-full px-3 py-2 border border-[#D1D5DC] rounded-[0.625rem] text-base bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                    </div>

                    {/* Current Support Coordinator */}
                    <div>
                      <label className="block text-base font-semibold text-[#364153] mb-2">
                        Current Support Coordinator
                      </label>
                      <input
                        type="text"
                        value={currentCoordinator}
                        readOnly
                        className="w-full px-3 py-2 border border-[#D1D5DC] rounded-[0.625rem] text-base bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                    </div>
                  </>
                )}

                {/* Transfer To */}
                {participantToTransfer && (
                  <div>
                    <label className="block text-base font-semibold text-[#364153] mb-2">
                      Transfer To
                    </label>
                    <div className="relative">
                      <select
                        value={selectedAssignee}
                        onChange={handleAssigneeChange}
                        className="w-full px-3 py-2 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500 appearance-none bg-white"
                        required
                      >
                        <option value="">Select support coordinator</option>
                        {assigneeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#E5E7EB]">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 bg-[#F3F4F6] text-[#364153] font-poppins text-base font-medium rounded-[0.625rem] hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !selectedAssignee || !participantToTransfer}
                  className="px-6 py-2 bg-[#7E57C2] text-white font-poppins text-base font-medium rounded-[0.625rem] hover:bg-[#6D4CAB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Transferring..." : "Transfer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransferParticipantModal;

